import { useMemo, useState } from "react";
import axios from "axios";
import Spinner from "../../../ui/spinner/Spinner";
import Checkbox from "../../../ui/checkbox/Checkbox";
import styles from "./OptionsStyles.module.css";

const nameValidation = (value) => {
  const trimmed = value.trim();
  return (
    trimmed.length >= 2 &&
    trimmed.length <= 30 &&
    /^[a-zA-Z]+([ '-][a-zA-Z]+)*$/.test(trimmed)
  );
};

const capitalizeName = (value) => {
  return value
    .trim()
    .toLowerCase()
    .split(/([ '-])/)
    .map((part) => {
      if (part === " " || part === "-" || part === "'") return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("");
};

const regexEmail = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const emailValidation = (value) => {
  const normalized = value.trim().toLowerCase();
  return (
    regexEmail.test(normalized) &&
    normalized.length >= 6 &&
    normalized.length <= 72
  );
};

const regexUsername = /^[a-zA-Z0-9]{8,15}$/;
const usernameValidation = (value) => regexUsername.test(value.trim());

const normalizePhone = (value) => value.replace(/[^\d+]/g, "");
const phoneValidation = (value) =>
  /^\+[1-9]\d{8,16}$/.test(normalizePhone(value));

const regexPassword =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,24}$/;
const passwordValidation = (value) =>
  regexPassword.test(value.trim()) &&
  !value.trim().toLowerCase().includes("password");

const regexSecret = /^.{2,50}$/;
const stringValidation = (value) => regexSecret.test(value.trim());

const METHOD_CONFIG = {
  username: {
    heading: "Sign up with username",
    uidLabel: "Username",
    uidId: "username",
    uidPlaceholder: "Enter your username",
    uidInputType: "text",
    uidMinLength: 8,
    uidMaxLength: 15,
    uidHelpTitle: "Username:",
    uidHelpItems: [
      "Username is unique and case insensitive.",
      "It must be 8-15 characters.",
      "It must contain letters and numbers only.",
      "It must not contain spaces or special characters.",
    ],
    normalizeForInput: (value) => value.trim().toLowerCase(),
    normalizeForSubmit: (value) => value.trim().toLowerCase(),
    validateUid: usernameValidation,
    uidErrorText: "Your username is invalid",
    completionMethod: "username",
  },

  email: {
    heading: "Sign up with your e-mail",
    uidLabel: "Email",
    uidId: "email",
    uidPlaceholder: "Enter your email",
    uidInputType: "email",
    uidMinLength: 6,
    uidMaxLength: 72,
    uidHelpTitle: "Email:",
    uidHelpItems: [
      "Your email is for sign in and to contact us only.",
      "In future, password help support may be sent to this email.",
      "If you forget your password, a wrong email may prevent you from getting help.",
    ],
    normalizeForInput: (value) => value.trim().toLowerCase(),
    normalizeForSubmit: (value) => value.trim().toLowerCase(),
    validateUid: emailValidation,
    uidErrorText: "Your email is invalid",
    completionMethod: "email",
  },

  phone: {
    heading: "Sign up with your phone number",
    uidLabel: "Phone Number",
    uidId: "phone",
    uidPlaceholder: "Enter your phone number",
    uidInputType: "text",
    uidMinLength: 8,
    uidMaxLength: 17,
    uidHelpTitle: "Phone Number:",
    uidHelpItems: [
      "Your phone number is for sign in and to contact us only.",
      "Password help support may be sent to this phone number.",
      "If you forget your password, a wrong phone number may prevent you from getting help.",
      "Format: + country code and your number. If any, omit the 0 that precedes your number.",
    ],
    normalizeForInput: normalizePhone,
    normalizeForSubmit: normalizePhone,
    validateUid: phoneValidation,
    uidErrorText: "Your phone number is invalid",
    completionMethod: "phone",
  },
};

const SignupBase = ({ method, onCancel, onCompletion }) => {
  const config = useMemo(() => METHOD_CONFIG[method], [method]);

  const [loading, setLoading] = useState(false);
  const [passwordHelp, setPasswordHelp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [userData, setUserData] = useState({
    firstname: "",
    lastname: "",
    callname: "",
    uid: "",
    password: "",
  });

  const [repeatPassword, setRepeatPassword] = useState("");

  const [secretData, setSecretData] = useState({
    secretQuestion1: "",
    secretAnswer1: "",
    secretQuestion2: "",
    secretAnswer2: "",
  });

  const [errors, setErrors] = useState({
    firstname: false,
    lastname: false,
    callname: false,
    uid: false,
    password: "",
    repeatPassword: "",
    secretQuestion1: false,
    secretAnswer1: false,
    secretQuestion2: false,
    secretAnswer2: false,
  });

  if (!config) {
    return <div className={styles.classes}>Invalid signup method.</div>;
  }

  const clearStep1Errors = () => {
    setErrors((prev) => ({
      ...prev,
      firstname: false,
      lastname: false,
      callname: false,
      uid: false,
      password: "",
      repeatPassword: "",
    }));
  };

  const clearStep2Errors = () => {
    setErrors((prev) => ({
      ...prev,
      secretQuestion1: false,
      secretAnswer1: false,
      secretQuestion2: false,
      secretAnswer2: false,
    }));
  };

  const handleNameChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: capitalizeName(value),
    }));
  };

  const handleUidChange = (value) => {
    setUserData((prev) => ({
      ...prev,
      uid: config.normalizeForInput(value),
    }));
  };

  const validateUserData = () => {
    const nextErrors = {
      firstname: false,
      lastname: false,
      callname: false,
      uid: false,
      password: "",
      repeatPassword: "",
    };

    const normalizedFirstname = userData.firstname.trim();
    const normalizedLastname = userData.lastname.trim();
    const normalizedCallname = userData.callname.trim();
    const normalizedUid = userData.uid.trim();
    const normalizedPassword = userData.password.trim();
    const normalizedRepeatPassword = repeatPassword.trim();

    if (
      !normalizedFirstname ||
      !normalizedLastname ||
      !normalizedCallname ||
      !normalizedUid ||
      !normalizedPassword ||
      !normalizedRepeatPassword
    ) {
      window.alert("Please fill all fields correctly.");
      return false;
    }

    if (!nameValidation(normalizedFirstname)) nextErrors.firstname = true;
    if (!nameValidation(normalizedLastname)) nextErrors.lastname = true;
    if (!nameValidation(normalizedCallname)) nextErrors.callname = true;
    if (!config.validateUid(normalizedUid)) nextErrors.uid = true;

    if (!passwordValidation(normalizedPassword)) {
      nextErrors.password =
        "Your password is invalid. Please read the note above on password requirement.";
    }

    if (!passwordValidation(normalizedRepeatPassword)) {
      nextErrors.repeatPassword =
        "Your repeat password is invalid. Please read the note above on password requirement.";
    }

    if (
      !nextErrors.password &&
      !nextErrors.repeatPassword &&
      normalizedPassword !== normalizedRepeatPassword
    ) {
      nextErrors.repeatPassword = "Both passwords must be the same.";
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));

    const hasError =
      nextErrors.firstname ||
      nextErrors.lastname ||
      nextErrors.callname ||
      nextErrors.uid ||
      nextErrors.password ||
      nextErrors.repeatPassword;

    return !hasError;
  };

  const handleUserData = async () => {
    clearStep1Errors();

    if (!validateUserData()) return;

    const payload = {
      firstname: userData.firstname.trim(),
      lastname: userData.lastname.trim(),
      callname: userData.callname.trim(),
      uid: config.normalizeForSubmit(userData.uid),
      password: userData.password.trim(),
    };

    console.log(" payload ", payload);

    try {
      setLoading(true);

      const response = await axios.post("php/sign_in/signup.php", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = response.data;
      
      console.log("RAW RESPONSE", response);
      console.log("DATA", data);

      if (data.code !== 200) {
        if (data.code === 409) {
          alert(
            "That sign-in identifier is not available. Please use another one",
          );
        }
        if (data.code === 401) {
          alert(
            data?.message ||
              "Invalid credentials. Retry or use password help",
          );
        } else {
          alert(
            data?.message || "Signup failed.",
          );
        }
        return;
      }

      alert(
        "Congrats. You have been registered. Please remember and keep safe your sign-in details.",
      );
      setPasswordHelp(true);
      return;
      
    } catch (error) {
      const code = error?.response?.code;
      const message = error?.response?.data?.message;

      if (code === 409) {
        window.alert(
          "That sign-in identifier is not available. Please use another one.",
        );
      } else if (code === 400 || code === 401) {
        window.alert(message || "Invalid request.");
      } else if (code === 500) {
        window.alert(message || "Server error. Please retry.");
      } else {
        window.alert("Network error. Please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateSecretData = () => {
    const nextErrors = {
      secretQuestion1: false,
      secretAnswer1: false,
      secretQuestion2: false,
      secretAnswer2: false,
    };

    const q1 = secretData.secretQuestion1.trim();
    const a1 = secretData.secretAnswer1.trim();
    const q2 = secretData.secretQuestion2.trim();
    const a2 = secretData.secretAnswer2.trim();

    if (!q1 || !a1 || !q2 || !a2) {
      window.alert("Please fill all fields.");
      return false;
    }

    if (!stringValidation(q1)) nextErrors.secretQuestion1 = true;
    if (!stringValidation(a1)) nextErrors.secretAnswer1 = true;
    if (!stringValidation(q2)) nextErrors.secretQuestion2 = true;
    if (!stringValidation(a2)) nextErrors.secretAnswer2 = true;

    if (q1.toLowerCase() === q2.toLowerCase()) {
      nextErrors.secretQuestion2 = true;
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));

    const hasError =
      nextErrors.secretQuestion1 ||
      nextErrors.secretAnswer1 ||
      nextErrors.secretQuestion2 ||
      nextErrors.secretAnswer2;

    return !hasError;
  };

  const submitHandler = async () => {
    clearStep2Errors();

    if (!validateSecretData()) return;

    try {
      setLoading(true);

      const response = await axios.post(
        "php/sign_in/update_password_help.php",
        {
          secretQuestion1: secretData.secretQuestion1.trim(),
          secretAnswer1: secretData.secretAnswer1.trim().toLowerCase(),
          secretQuestion2: secretData.secretQuestion2.trim(),
          secretAnswer2: secretData.secretAnswer2.trim().toLowerCase(),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      console.log("PASSWORD HELP RAW RESPONSE", response);
      console.log("PASSWORD HELP DATA", response.data);

      if (data.code !== 200) {
        window.alert(data.message || "Failed to save security questions.");
        return;
      }

      alert(data.message || "Security data saved successfully");
      onCompletion({
        method: config.completionMethod,
        callname: capitalizeName(userData.callname),
      });
      
    } catch (error) {
      const code = error?.response?.code;
      const message = error?.response?.data?.message;

      if (code === 400 || code === 401) {
        window.alert(message || "Invalid request.");
      } else if (code === 500) {
        window.alert(message || "Server error. Please retry.");
      } else {
        window.alert("Network error. Please retry.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.classes}>
      <header>
        <h2>{config.heading}</h2>
      </header>
      <p>
        <button
          className={`${styles.bgRed} ${styles.floatRight}`}
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </p>

      {!passwordHelp && (
        <div>
          <ol className={styles.ol}>
            <strong>{config.uidHelpTitle}</strong>
            <br />
            {config.uidHelpItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>

          <ol>
            <strong>Name:</strong>
            <br />
            <li>
              Must be 2–30 characters and may include letters, spaces, hyphens
              (-) or apostrophes (').
            </li>
          </ol>

          <ol>
            <strong>Password:</strong>
            <br />
            <li>Must be between 8-24 characters and is case sensitive.</li>
            <li>It must not contain spaces or the word "password".</li>
            <li>
              Must contain at least one uppercase, one lowercase, one number,
              and one special character.
            </li>
          </ol>

          <div className={styles.inputView}>
            <label htmlFor="firstname">Firstname</label>
            <input
              id="firstname"
              placeholder="Enter your firstname"
              minLength={2}
              maxLength={30}
              required
              autoComplete="off"
              value={userData.firstname}
              onChange={(e) => handleNameChange("firstname", e.target.value)}
            />
            {errors.firstname && (
              <span className={styles.errorText}>
                Your firstname is invalid
              </span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="lastname">Lastname</label>
            <input
              id="lastname"
              placeholder="Enter your lastname"
              minLength={2}
              maxLength={30}
              required
              autoComplete="off"
              value={userData.lastname}
              onChange={(e) => handleNameChange("lastname", e.target.value)}
            />
            {errors.lastname && (
              <span className={styles.errorText}>Your lastname is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="callname">Callname</label>
            <input
              id="callname"
              placeholder="Enter your callname"
              minLength={2}
              maxLength={30}
              required
              autoComplete="off"
              value={userData.callname}
              onChange={(e) => handleNameChange("callname", e.target.value)}
            />
            {errors.callname && (
              <span className={styles.errorText}>Your callname is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor={config.uidId}>{config.uidLabel}</label>
            <input
              id={config.uidId}
              type={config.uidInputType}
              placeholder={config.uidPlaceholder}
              minLength={config.uidMinLength}
              maxLength={config.uidMaxLength}
              required
              autoComplete="off"
              value={userData.uid}
              onChange={(e) => handleUidChange(e.target.value)}
            />
            {errors.uid && (
              <span className={styles.errorText}>{config.uidErrorText}</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="password">Password</label>
            <div className={styles.flex}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Enter your password"
                minLength={8}
                maxLength={24}
                required
                autoComplete="off"
                value={userData.password}
                onChange={(e) =>
                  setUserData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
              />
              <Checkbox
                labelClassname={styles.checkboxLabel}
                inputClassname={styles.checkbox}
                label="Show"
                checked={showPassword}
                onChange={() => setShowPassword((prev) => !prev)}
              />
            </div>
            {errors.password && (
              <span className={styles.errorText}>{errors.password}</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="repeatPassword">Repeat password</label>
            <div className={styles.flex}>
              <input
                type={showRepeatPassword ? "text" : "password"}
                id="repeatPassword"
                placeholder="Repeat your password"
                minLength={8}
                maxLength={24}
                required
                autoComplete="off"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
              />
              <Checkbox
                labelClassname={styles.checkboxLabel}
                inputClassname={styles.checkbox}
                label="Show"
                checked={showRepeatPassword}
                onChange={() => setShowRepeatPassword((prev) => !prev)}
              />
            </div>
            {errors.repeatPassword && (
              <span className={styles.errorText}>{errors.repeatPassword}</span>
            )}
          </div>

          <button
            className={`${styles.bgGreen} ${styles.btnTBMargins}`}
            onClick={handleUserData}
            type="button"
          >
            Register
          </button>
        </div>
      )}

      {passwordHelp && (
        <div>
          <header>
            <h2>Password help</h2>
          </header>

          <ol>
            <li>
              In case you forget your password, we can help you recover it.
            </li>
            <li>
              Please provide below 2 questions we will ask you and their
              answers.
            </li>
            <li>
              Each secret question and answer must be between 2-50 characters.
            </li>
            <li>The two questions must not be the same.</li>
          </ol>

          <div className={styles.inputView}>
            <label htmlFor="secretQuestion1">Secret question 1</label>
            <input
              id="secretQuestion1"
              placeholder="Enter your secret question 1"
              value={secretData.secretQuestion1}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretQuestion1: e.target.value.trim(),
                }))
              }
            />
            {errors.secretQuestion1 && (
              <span className={styles.errorText}>Question is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="secretAnswer1">Secret answer 1</label>
            <input
              id="secretAnswer1"
              placeholder="Enter your secret answer 1"
              value={secretData.secretAnswer1}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretAnswer1: e.target.value.trim(),
                }))
              }
            />
            {errors.secretAnswer1 && (
              <span className={styles.errorText}>Answer is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="secretQuestion2">Secret question 2</label>
            <input
              id="secretQuestion2"
              placeholder="Enter your secret question 2"
              value={secretData.secretQuestion2}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretQuestion2: e.target.value.trim(),
                }))
              }
            />
            {errors.secretQuestion2 && (
              <span className={styles.errorText}>
                Question is invalid. Note that both questions cannot be the
                same.
              </span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="secretAnswer2">Secret answer 2</label>
            <input
              id="secretAnswer2"
              placeholder="Enter your secret answer 2"
              value={secretData.secretAnswer2}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretAnswer2: e.target.value.trim(),
                }))
              }
            />
            {errors.secretAnswer2 && (
              <span className={styles.errorText}>Answer is invalid</span>
            )}
          </div>

          <button
            className={`${styles.bgGreen} ${styles.btnTBMargins}`}
            onClick={submitHandler}
            type="button"
          >
            Submit
          </button>
        </div>
      )}

      {loading && <Spinner />}
    </div>
  );
};

export default SignupBase;

/*
import { useMemo, useState } from "react";
import axios from "axios";
import Spinner from "../../../ui/spinner/Spinner";
import Checkbox from "../../../ui/checkbox/Checkbox";
import styles from "./OptionsStyles.module.css";

const nameValidation = (value) => {
  const trimmed = value.trim();
  return (
    trimmed.length >= 2 &&
    trimmed.length <= 30 &&
    /^[a-zA-Z]+([ '-][a-zA-Z]+)*$/.test(trimmed)
  );
};

const capitalizeName = (value) => {
  return value
    .trim()
    .toLowerCase()
    .split(/([ '-])/)
    .map((part) => {
      if (part === " " || part === "-" || part === "'") return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("");
};

const regexEmail = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const emailValidation = (value) => {
  const normalized = value.trim().toLowerCase();
  return (
    regexEmail.test(normalized) &&
    normalized.length >= 6 &&
    normalized.length <= 72
  );
};

const regexUsername = /^[a-zA-Z0-9]{8,15}$/;
const usernameValidation = (value) => regexUsername.test(value.trim());

const normalizePhone = (value) => value.replace(/[^\d+]/g, "");
const phoneValidation = (value) =>
  /^\+[1-9]\d{6,14}$/.test(normalizePhone(value));

const regexPassword =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,24}$/;
const passwordValidation = (value) =>
  regexPassword.test(value.trim()) &&
  !value.trim().toLowerCase().includes("password");

const regexSecret = /^.{2,50}$/;
const stringValidation = (value) => regexSecret.test(value.trim());

const METHOD_CONFIG = {
  username: {
    heading: "Sign up with username",
    uidLabel: "Username",
    uidId: "username",
    uidPlaceholder: "Enter your username",
    uidInputType: "text",
    uidMinLength: 8,
    uidMaxLength: 15,
    uidHelpTitle: "Username:",
    uidHelpItems: [
      "Username is unique and case insensitive.",
      "It must be 8-15 characters.",
      "It must contain letters and numbers only.",
      "It must not contain spaces or special characters.",
    ],
    normalizeForInput: (value) => value.trim().toLowerCase(),
    normalizeForSubmit: (value) => value.trim().toLowerCase(),
    validateUid: usernameValidation,
    uidErrorText: "Your username is invalid",
    unavailableMessage:
      "Your username is not available. If you are the owner of this username, then sign in as a registered user using your username and password. Else, use another username to register.",
    completionMethod: "username",
  },

  email: {
    heading: "Sign up with your e-mail",
    uidLabel: "Email",
    uidId: "email",
    uidPlaceholder: "Enter your email",
    uidInputType: "email",
    uidMinLength: 6,
    uidMaxLength: 72,
    uidHelpTitle: "Email:",
    uidHelpItems: [
      "Your email is for sign in and to contact us only.",
      "In future, password help support may be sent to this email.",
      "If you forget your password, a wrong email may prevent you from getting help.",
    ],
    normalizeForInput: (value) => value.trim().toLowerCase(),
    normalizeForSubmit: (value) => value.trim().toLowerCase(),
    validateUid: emailValidation,
    uidErrorText: "Your email is invalid",
    unavailableMessage:
      "Your email is not available. If you are the owner of this email, then sign in as a registered user using your email and password. Else, use another email to register.",
    completionMethod: "email",
  },

  phone: {
    heading: "Sign up with your phone number",
    uidLabel: "Phone Number",
    uidId: "phone",
    uidPlaceholder: "Enter your phone number",
    uidInputType: "text",
    uidMinLength: 8,
    uidMaxLength: 16,
    uidHelpTitle: "Phone Number:",
    uidHelpItems: [
      "Your phone number is for sign in and to contact us only.",
      "Password help support may be sent to this phone number.",
      "If you forget your password, a wrong phone number may prevent you from getting help.",
      "Format: + country code and your number. If any, omit the 0 that precedes your number.",
    ],
    normalizeForInput: (value) => normalizePhone(value),
    normalizeForSubmit: (value) => normalizePhone(value),
    validateUid: phoneValidation,
    uidErrorText: "Your phone number is invalid",
    unavailableMessage:
      "Your phone number is not available. If you are the owner of this phone number, then sign in as a registered user using your phone number and password. Else, use another phone number to register.",
    completionMethod: "phone",
  },
};

const SignupBase = ({ method, onCancel, onCompletion }) => {
  const config = useMemo(() => METHOD_CONFIG[method], [method]);

  const [loading, setLoading] = useState(false);
  const [passwordHelp, setPasswordHelp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const [userData, setUserData] = useState({
    firstname: "",
    lastname: "",
    callname: "",
    uid: "",
    password: "",
  });

  const [repeatPassword, setRepeatPassword] = useState("");

  const [secretData, setSecretData] = useState({
    secretQuestion1: "",
    secretAnswer1: "",
    secretQuestion2: "",
    secretAnswer2: "",
  });

  const [errors, setErrors] = useState({
    firstname: false,
    lastname: false,
    callname: false,
    uid: false,
    password: "",
    repeatPassword: "",
    secretQuestion1: false,
    secretAnswer1: false,
    secretQuestion2: false,
    secretAnswer2: false,
  });

  if (!config) {
    return <div className={styles.classes}>Invalid signup method.</div>;
  }

  const clearStep1Errors = () => {
    setErrors((prev) => ({
      ...prev,
      firstname: false,
      lastname: false,
      callname: false,
      uid: false,
      password: "",
      repeatPassword: "",
    }));
  };

  const clearStep2Errors = () => {
    setErrors((prev) => ({
      ...prev,
      secretQuestion1: false,
      secretAnswer1: false,
      secretQuestion2: false,
      secretAnswer2: false,
    }));
  };

  const handleNameChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: capitalizeName(value),
    }));
  };

  const handleUidChange = (value) => {
    setUserData((prev) => ({
      ...prev,
      uid: config.normalizeForInput(value),
    }));
  };

  const validateUserData = () => {
    const nextErrors = {
      firstname: false,
      lastname: false,
      callname: false,
      uid: false,
      password: "",
      repeatPassword: "",
    };

    const normalizedFirstname = userData.firstname.trim();
    const normalizedLastname = userData.lastname.trim();
    const normalizedCallname = userData.callname.trim();
    const normalizedUid = userData.uid.trim();
    const normalizedPassword = userData.password.trim();
    const normalizedRepeatPassword = repeatPassword.trim();

    if (
      !normalizedFirstname ||
      !normalizedLastname ||
      !normalizedCallname ||
      !normalizedUid ||
      !normalizedPassword ||
      !normalizedRepeatPassword
    ) {
      window.alert("Please fill all fields correctly.");
      return false;
    }

    if (!nameValidation(normalizedFirstname)) nextErrors.firstname = true;
    if (!nameValidation(normalizedLastname)) nextErrors.lastname = true;
    if (!nameValidation(normalizedCallname)) nextErrors.callname = true;
    if (!config.validateUid(normalizedUid)) nextErrors.uid = true;

    if (!passwordValidation(normalizedPassword)) {
      nextErrors.password =
        "Your password is invalid. Please read the note above on password requirement.";
    }

    if (!passwordValidation(normalizedRepeatPassword)) {
      nextErrors.repeatPassword =
        "Your repeat password is invalid. Please read the note above on password requirement.";
    }

    if (
      !nextErrors.password &&
      !nextErrors.repeatPassword &&
      normalizedPassword !== normalizedRepeatPassword
    ) {
      nextErrors.repeatPassword = "Both passwords must be the same.";
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));

    const hasError =
      nextErrors.firstname ||
      nextErrors.lastname ||
      nextErrors.callname ||
      nextErrors.uid ||
      nextErrors.password ||
      nextErrors.repeatPassword;

    return !hasError;
  };

  const handleUserData = async () => {
    clearStep1Errors();

    if (!validateUserData()) return;

    const payload = {
      firstname: userData.firstname.trim(),
      lastname: userData.lastname.trim(),
      callname: userData.callname.trim(),
      uid: config.normalizeForSubmit(userData.uid),
      password: userData.password.trim(),
    };

    try {
      setLoading(true);

      const response = await axios.post("php/sign_in/signup.php", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = response.data;
      console.log("signup response:", data);

      if (data === "Access denied" || data === 0) {
        window.alert(
          "Error connecting to server. Check your internet connection or retry later.",
        );
        return;
      }

      if (data === 1) {
        window.alert(config.unavailableMessage);
        return;
      }

      if (data === "ok") {
        window.alert(
          "Congrats. You have been registered. Please remember and keep safe your sign-in details.",
        );
        setPasswordHelp(true);
      }
    } catch (error) {
      window.alert(`Error: ${error} Please retry.`);
    } finally {
      setLoading(false);
    }
  };

  const validateSecretData = () => {
    const nextErrors = {
      secretQuestion1: false,
      secretAnswer1: false,
      secretQuestion2: false,
      secretAnswer2: false,
    };

    const q1 = secretData.secretQuestion1.trim();
    const a1 = secretData.secretAnswer1.trim();
    const q2 = secretData.secretQuestion2.trim();
    const a2 = secretData.secretAnswer2.trim();

    if (!q1 || !a1 || !q2 || !a2) {
      window.alert("Please fill all fields.");
      return false;
    }

    if (!stringValidation(q1)) nextErrors.secretQuestion1 = true;
    if (!stringValidation(a1)) nextErrors.secretAnswer1 = true;
    if (!stringValidation(q2)) nextErrors.secretQuestion2 = true;
    if (!stringValidation(a2)) nextErrors.secretAnswer2 = true;

    if (q1.toLowerCase() === q2.toLowerCase()) {
      nextErrors.secretQuestion2 = true;
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));

    const hasError =
      nextErrors.secretQuestion1 ||
      nextErrors.secretAnswer1 ||
      nextErrors.secretQuestion2 ||
      nextErrors.secretAnswer2;

    return !hasError;
  };

  const submitHandler = async () => {
    clearStep2Errors();

    if (!validateSecretData()) return;

    try {
      setLoading(true);

      const response = await axios.post(
        "php/sign_in/update_password_help.php",
        {
          secretQuestion1: secretData.secretQuestion1.trim(),
          secretAnswer1: secretData.secretAnswer1.trim().toLowerCase(),
          secretQuestion2: secretData.secretQuestion2.trim(),
          secretAnswer2: secretData.secretAnswer2.trim().toLowerCase(),
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      console.log("password help response:", data);

      if (data === "Access denied" || data === 0 || data === "Empty field(s)") {
        window.alert("Error connecting to server. Retry.");
        return;
      }

      if (data === "ok") {
        onCompletion?.({
          method: config.completionMethod,
          callname: capitalizeName(userData.callname),
        });
      }
    } catch (error) {
      window.alert(`Error: ${error} Please retry.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.classes}>
      <header>
        <h2>{config.heading}</h2>
      </header>
      <p>
        <button
          className={`${styles.bgRed} ${styles.floatRight}`}
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </p>

      {!passwordHelp && (
        <div>
          <ol className={styles.ol}>
            <strong>{config.uidHelpTitle}</strong>
            <br />
            {config.uidHelpItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>

          <ol>
            <strong>Name:</strong>
            <br />
            <li>
              Must be 2–30 characters and may include letters, spaces, hyphens
              (-) or apostrophes (')
            </li>
          </ol>

          <ol>
            <strong>Password:</strong>
            <br />
            <li>Must be between 8-24 characters and is case sensitive.</li>
            <li>It must not contain spaces or the word "password".</li>
            <li>
              Must contain at least one uppercase, one lowercase, one number,
              and one special character.
            </li>
          </ol>

          <div className={styles.inputView}>
            <label htmlFor="firstname">Firstname</label>
            <input
              id="firstname"
              placeholder="Enter your firstname"
              minLength={2}
              maxLength={30}
              required
              autoComplete="off"
              value={userData.firstname}
              onChange={(e) => handleNameChange("firstname", e.target.value)}
            />
            {errors.firstname && (
              <span className={styles.errorText}>
                Your firstname is invalid
              </span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="lastname">Lastname</label>
            <input
              id="lastname"
              placeholder="Enter your lastname"
              minLength={2}
              maxLength={30}
              required
              autoComplete="off"
              value={userData.lastname}
              onChange={(e) => handleNameChange("lastname", e.target.value)}
            />
            {errors.lastname && (
              <span className={styles.errorText}>Your lastname is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="callname">Callname</label>
            <input
              id="callname"
              placeholder="Enter your callname"
              minLength={2}
              maxLength={30}
              required
              autoComplete="off"
              value={userData.callname}
              onChange={(e) => handleNameChange("callname", e.target.value)}
            />
            {errors.callname && (
              <span className={styles.errorText}>Your callname is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor={config.uidId}>{config.uidLabel}</label>
            <input
              id={config.uidId}
              type={config.uidInputType}
              placeholder={config.uidPlaceholder}
              minLength={config.uidMinLength}
              maxLength={config.uidMaxLength}
              required
              autoComplete="off"
              value={userData.uid}
              onChange={(e) => handleUidChange(e.target.value)}
            />
            {errors.uid && (
              <span className={styles.errorText}>{config.uidErrorText}</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="password">Password</label>
            <div className={styles.flex}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Enter your password"
                minLength={8}
                maxLength={24}
                required
                autoComplete="off"
                value={userData.password}
                onChange={(e) =>
                  setUserData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
              />
              <Checkbox
                labelClassname={styles.checkboxLabel}
                inputClassname={styles.checkbox}
                label="Show"
                checked={showPassword}
                onChange={() => setShowPassword((prev) => !prev)}
              />
            </div>
            {errors.password && (
              <span className={styles.errorText}>{errors.password}</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="repeatPassword">Repeat password</label>
            <div className={styles.flex}>
              <input
                type={showRepeatPassword ? "text" : "password"}
                id="repeatPassword"
                placeholder="Repeat your password"
                minLength={8}
                maxLength={24}
                required
                autoComplete="off"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
              />
              <Checkbox
                labelClassname={styles.checkboxLabel}
                inputClassname={styles.checkbox}
                label="Show"
                checked={showRepeatPassword}
                onChange={() => setShowRepeatPassword((prev) => !prev)}
              />
            </div>
            {errors.repeatPassword && (
              <span className={styles.errorText}>{errors.repeatPassword}</span>
            )}
          </div>

          <button
            className={`${styles.bgGreen} ${styles.btnTBMargins}`}
            onClick={handleUserData}
            type="button"
          >
            Register
          </button>
        </div>
      )}

      {passwordHelp && (
        <div>
          <header>
            <h2>Password help</h2>
          </header>

          <ol>
            <li>
              In case you forget your password, we can help you recover it.
            </li>
            <li>
              Please provide below 2 questions we will ask you and their
              answers.
            </li>
            <li>
              Each secret question and answer must be between 2-50 characters.
            </li>
            <li>The two questions must not be the same.</li>
          </ol>

          <div className={styles.inputView}>
            <label htmlFor="secretQuestion1">Secret question 1</label>
            <input
              id="secretQuestion1"
              placeholder="Enter your secret question 1"
              value={secretData.secretQuestion1}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretQuestion1: e.target.value.trim(),
                }))
              }
            />
            {errors.secretQuestion1 && (
              <span className={styles.errorText}>Question is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="secretAnswer1">Secret answer 1</label>
            <input
              id="secretAnswer1"
              placeholder="Enter your secret answer 1"
              value={secretData.secretAnswer1}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretAnswer1: e.target.value.trim(),
                }))
              }
            />
            {errors.secretAnswer1 && (
              <span className={styles.errorText}>Answer is invalid</span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="secretQuestion2">Secret question 2</label>
            <input
              id="secretQuestion2"
              placeholder="Enter your secret question 2"
              value={secretData.secretQuestion2}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretQuestion2: e.target.value.trim(),
                }))
              }
            />
            {errors.secretQuestion2 && (
              <span className={styles.errorText}>
                Question is invalid. Note that both questions cannot be the
                same.
              </span>
            )}
          </div>

          <div className={styles.inputView}>
            <label htmlFor="secretAnswer2">Secret answer 2</label>
            <input
              id="secretAnswer2"
              placeholder="Enter your secret answer 2"
              value={secretData.secretAnswer2}
              autoCapitalize="none"
              autoComplete="off"
              maxLength={50}
              onChange={(e) =>
                setSecretData((prev) => ({
                  ...prev,
                  secretAnswer2: e.target.value.trim(),
                }))
              }
            />
            {errors.secretAnswer2 && (
              <span className={styles.errorText}>Answer is invalid</span>
            )}
          </div>

          <button
            className={`${styles.bgGreen} ${styles.btnTBMargins}`}
            onClick={submitHandler}
            type="button"
          >
            Submit
          </button>
        </div>
      )}

      {loading && <Spinner />}
    </div>
  );
};

export default SignupBase;
*/
